import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Brain, Plus, LogOut, Loader2, FileText, TrendingUp, Users } from 'lucide-react';
import { AuthContext } from '../App';
import WaitlistManager from './WaitlistManager';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [concepts, setConcepts] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('concepts');

  useEffect(() => {
    loadData();
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setIsAdmin(response.data.is_admin || false);
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const loadData = async () => {
    try {
      const [conceptsRes, simulationsRes] = await Promise.all([
        axios.get(`${API}/concepts`, { withCredentials: true }),
        axios.get(`${API}/simulations`, { withCredentials: true })
      ]);
      setConcepts(conceptsRes.data);
      setSimulations(simulationsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      {/* Navigation */}
      <nav className="border-b border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Brain className="w-8 h-8 text-indigo-600" />
            <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-cyan-600 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              IntentAI
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600" data-testid="user-name">Hi, {user?.name}</span>
            <Button
              onClick={handleLogout}
              variant="outline"
              data-testid="logout-button"
              className="rounded-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex justify-between items-center mb-10">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Dashboard</h1>
            <p className="text-slate-600">Manage your product concepts and simulations</p>
          </div>
          <Button
            onClick={() => navigate('/concept/new')}
            data-testid="new-concept-button"
            className="bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700 text-white rounded-full px-6 py-3 font-semibold shadow-lg"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Concept
          </Button>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total Concepts</p>
                  <p className="text-3xl font-bold text-slate-800" data-testid="total-concepts">{concepts.length}</p>
                </div>
                <FileText className="w-10 h-10 text-indigo-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total Simulations</p>
                  <p className="text-3xl font-bold text-slate-800" data-testid="total-simulations">{simulations.length}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-cyan-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Completed Tests</p>
                  <p className="text-3xl font-bold text-slate-800" data-testid="completed-simulations">
                    {simulations.filter(s => s.status === 'completed').length}
                  </p>
                </div>
                <Brain className="w-10 h-10 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Concepts and Waitlist */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className={isAdmin ? "grid w-full grid-cols-2 max-w-md" : "grid w-full grid-cols-1 max-w-md"}>
            <TabsTrigger value="concepts" className="gap-2">
              <FileText className="w-4 h-4" />
              Concepts & Simulations
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="waitlist" className="gap-2">
                <Users className="w-4 h-4" />
                Waitlist
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="concepts" className="space-y-8">
            {/* Recent Simulations */}
            {simulations.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Recent Simulations</h2>
                <div className="space-y-4" data-testid="simulations-list">
                  {simulations.slice(0, 5).map((sim) => {
                    const concept = concepts.find(c => c.id === sim.product_concept_id);
                    return (
                      <Card key={sim.id} className="border-slate-200 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/simulation/${sim.id}`)} data-testid={`simulation-${sim.id}`}>
                        <CardContent className="pt-6">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-slate-800 mb-2">{concept?.name || 'Unknown Concept'}</h3>
                              <div className="flex items-center gap-4 text-sm text-slate-600">
                                <span>{sim.num_simulations} simulations</span>
                                {sim.status === 'completed' && sim.purchase_intent_score && (
                                  <span className="font-semibold text-indigo-600">Intent Score: {sim.purchase_intent_score.toFixed(1)}/5.0</span>
                                )}
                              </div>
                            </div>
                            <div>
                              {sim.status === 'running' && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                                  <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                  Running
                                </span>
                              )}
                              {sim.status === 'completed' && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                  Completed
                                </span>
                              )}
                              {sim.status === 'failed' && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                  Failed
                                </span>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State */}
            {concepts.length === 0 && (
              <Card className="border-2 border-dashed border-slate-300 bg-white" data-testid="empty-state">
                <CardContent className="py-16 text-center">
                  <Brain className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">No concepts yet</h3>
                  <p className="text-slate-600 mb-6">Create your first product concept to start testing purchase intent</p>
                  <Button
                    onClick={() => navigate('/concept/new')}
                    data-testid="empty-state-new-concept-button"
                    className="bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700 text-white rounded-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Concept
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {isAdmin && (
            <TabsContent value="waitlist">
              <WaitlistManager />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default Dashboard;